import { EventActivityClientChangeFeed } from '@hds-products/event-runtime-entities-client';
import { useAdminEventActivityStore } from '../pinia/adminEventActivityStore';
import { useAdminSdkStatusStore } from '../pinia/adminSdkStatusStore';
import { compare } from 'fast-json-patch';

class AdminEventActivityPiniaVueAdapter {
    #_changeFeed;
    #_deltaServerClient;
    #_eventActivityPiniaStore;
    #_sdkStatusPiniaStore;
    #_entityIdToWatcherIdMap = new Map();
    constructor(deltaServerClient, eventActivityBaseUri, customHeaderFn) {
        this.#_deltaServerClient = deltaServerClient;
        this.#_changeFeed = new EventActivityClientChangeFeed(deltaServerClient, eventActivityBaseUri, customHeaderFn, null);
        this.#_eventActivityPiniaStore = useAdminEventActivityStore();
        this.#_sdkStatusPiniaStore = useAdminSdkStatusStore();
        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-event-activity', { title: 'Event Activity', description: 'Real-time event lifecycle', status: 'INITIALIZING' })
        this.onEventActivityAdded = this.onEventActivityAdded.bind(this);
        this.onEventActivityUpdated = this.onEventActivityUpdated.bind(this);
        this.onEventActivityRemoved = this.onEventActivityRemoved.bind(this);
    }
    async _connectAndStartChangeFeed(isRestart = false) {
        let attempts = 1;
        if (this.#_deltaServerClient.isConnected()) {
            await this.#_changeFeed.whenStarted();
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-event-activity', { status: 'CONNECTED', attempts })
        } else {
            while (!this.#_deltaServerClient.isConnected()) {
                try {
                    await this.#_deltaServerClient.whenConnected();
                    if (attempts === 1) {
                        console.info(`admin-event-activity changefeed connected`);
                    } else {
                        console.info(`admin-event-activity changefeed connected after attempt #${attempts}.`);
                    }
                    if (isRestart) {
                        await this.#_changeFeed.restart();
                    } else {
                        await this.#_changeFeed.whenStarted();
                    }
                    attempts = 1;
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-event-activity', { status: 'CONNECTED', attempts })
                } catch (err) {
                    //A refused principal stays refused, so another attempt cannot succeed
                    if (err?.closeCode === 4403) {
                        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-event-activity', { status: 'DISCONNECTED', attempts })
                        return;
                    }
                    attempts++;
                    console.error(`Attempt #${attempts}: admin-event-activity changefeed error: ${err}`);
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-event-activity', { status: 'DISCONNECTED', attempts })
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }
    }
    async initialize() {
        this.#_changeFeed.addFailureListener(async (error) => {
            console.error(`Change Feed Failure (AdminEventActivityChangeFeed) => ${error.message}`);
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('admin-event-activity', { status: 'DISCONNECTED' })
            await this._connectAndStartChangeFeed(true);
        }, this);
        await this._connectAndStartChangeFeed();
    }
    async watchEventActivity(eventActivityId) {
        // Check if the entity watcher map does not already have a watcher for the provided eventActivityId
        if (!this.#_entityIdToWatcherIdMap.has(eventActivityId)) {
            const watcher = await this.#_changeFeed.watch(eventActivityId);
            watcher.on('entity-added', this.onEventActivityAdded);
            watcher.on('entity-updated', this.onEventActivityUpdated);
            watcher.on('entity-removed', this.onEventActivityRemoved);
            this.#_entityIdToWatcherIdMap.set(eventActivityId, watcher.watchId);
            const eventActivityEntity = watcher.getEntity(eventActivityId);
            if (eventActivityEntity) { await this.onEventActivityAdded(eventActivityEntity); }
        }
    }
    async unwatchEventActivity(eventActivityId) {
        // Check if the entity watcher map has a key for the provided eventActivityId
        if (this.#_entityIdToWatcherIdMap.has(eventActivityId)) {
            const watcherId = this.#_entityIdToWatcherIdMap.get(eventActivityId);
            const watcher = this.#_changeFeed.getWatcher(watcherId);
            watcher.off('entity-added', this.onEventActivityAdded);
            watcher.off('entity-updated', this.onEventActivityUpdated);
            watcher.off('entity-removed', this.onEventActivityRemoved);
            this.#_changeFeed.unwatch(watcherId);
            this.#_entityIdToWatcherIdMap.delete(eventActivityId);
            this.onEventActivityRemoved(eventActivityId);
        }
    }
    onEventActivityAdded(eventActivityEntity) {
        this.#_eventActivityPiniaStore.addEventActivity(eventActivityEntity.toJSON());
    }
    onEventActivityUpdated(eventActivityId, prevModel, newEntity) {
        const jsonPatch = compare(prevModel, newEntity.toJSON());
        if (jsonPatch.length > 0) {
            this.#_eventActivityPiniaStore.applyJsonPatch(eventActivityId, jsonPatch);
        }
    }
    onEventActivityRemoved(eventActivityId) {
        this.#_eventActivityPiniaStore.removeEventActivity(eventActivityId);
    }
}

export { AdminEventActivityPiniaVueAdapter };
export default AdminEventActivityPiniaVueAdapter;
